/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import ChevronLeftIcon from "assets/svgs/ChevronLeftIcon";
import ChevronRight16 from "assets/svgs/ChevronRight16";
import clsx from "clsx";
import JobListCard from "components/Job/JobListCard";
import useIntersectionObservers from "hooks/useIntersectionObservers";
import { useEffect, useRef, useState } from "react";
import { getUserId } from "utils";
import { categoryToTypeMapping } from "utils/jobFeedApiParser";
import Mixpanel from "utils/Mixpanel";

const Type5Module = ({ data, isCandidateExperienced, id }) => {
	const scrollContainerRef = useRef(null);
	const [scrollIndicator, setScrollIndicator] = useState(0);
	const [isLeftScrollDisabled, setIsLeftScrollDisabled] = useState(true);
	const [isRightScrollDisabled, setIsRightScrollDisabled] = useState(false);
	const [hasTrackedModuleSeen, setHasTrackedModuleSeen] = useState(false);

	const { isIntersecting, elementRef } = useIntersectionObservers({
		threshold: 0.2,
	});

	useEffect(() => {
		if (isIntersecting && data?.children.length > 0 && !hasTrackedModuleSeen) {
			Mixpanel.track("Floating jobs module seen", {
				"module title": data?.name,
			});
			setHasTrackedModuleSeen(true);
		}
	}, [isIntersecting, data?.children, hasTrackedModuleSeen]);

	const handleScrollLeft = () => {
		if (scrollContainerRef.current) {
			scrollContainerRef.current.scrollBy({
				left: -310, // Adjust scroll distance
				behavior: "smooth",
			});
		}
	};

	const handleScrollRight = () => {
		if (scrollContainerRef.current) {
			scrollContainerRef.current.scrollBy({
				left: 310, // Adjust scroll distance
				behavior: "smooth",
			});
		}
	};

	const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
		const { scrollLeft, scrollWidth, clientWidth } = e.currentTarget;
		const totalScrollableWidth = scrollWidth - clientWidth;

		setIsLeftScrollDisabled(scrollLeft <= 0);
		setIsRightScrollDisabled(scrollLeft >= totalScrollableWidth);

		const leftOffset = (scrollLeft / totalScrollableWidth) * 100;
		setScrollIndicator(leftOffset);
	};

	const handleUpdateProfileClick = () => {
		Mixpanel.track("Update Profile CTA on module clicked", {
			"user id": getUserId(),
		});

		const url = isCandidateExperienced
			? "/candidate/profile?profile_interaction=add_experience&set_experienced=false"
			: "/candidate/profile?profile_interaction=edit_preferred_job_title";

		window.location.href = url;
	};

	const handleSeeMoreJobsClick = () => {};

	const showUpdateProfileCTA = id?.includes("profile_based_jobs");
	const showSeeMoreJobsCTA = false && data?.children?.elements?.length > 10; // remove falsy condition after release of "see more jobs" functionality.
	return (
		<div
			ref={elementRef}
			className="mt-[16px] border border-solid md:rounded-[12px] md:shadow-100"
			style={{
				background: data?.ui_element?.background_colour,
				color: data?.ui_element?.text_colour,
			}}
		>
			<div className="flex-col items-start space-y-[16px] p-[16px] pr-0 md:flex md:flex-row md:items-center md:space-y-0">
				<div className="flex flex-col justify-center space-y-[4px] pr-[16px] md:h-full md:w-[134px] md:max-w-[134px]">
					<div className="flex justify-between">
						<p className="m-0 text-[20px] font-bold leading-[28px]">
							{data?.name}
						</p>
						{showSeeMoreJobsCTA && (
							<button
								type="button"
								className="ml-[8px] mr-[16px] flex w-fit justify-start rounded-[4px] py-[4px] text-[12px] font-[600] leading-[16px] outline-none md:hidden"
								onClick={handleSeeMoreJobsClick}
								style={{
									color: data?.ui_element?.viewall_colour,
								}}
							>
								{data?.view_all_string || "See more"}
								<ChevronRight16
									width="16px"
									height="16px"
									className="ml-[4px]"
									fillColor={data?.ui_element?.viewall_colour}
								/>
							</button>
						)}
					</div>
					<p
						className="m-0 text-[12px] leading-[16px]"
						style={{ color: data?.ui_element?.text_colour }}
					>
						{data?.subtitle}
					</p>
					<div className="md:w-[100px]">
						{showUpdateProfileCTA && (
							<button
								type="button"
								className="mt-[16px] hidden w-full rounded-[4px] bg-[#FFFFFF] px-[8px] py-[4px] text-[12px] font-[600] leading-[16px] md:block"
								onClick={handleUpdateProfileClick}
								style={{
									color: data?.ui_element?.text_colour || "#5357CF",
								}}
							>
								Update profile
							</button>
						)}
						{showSeeMoreJobsCTA && (
							<button
								type="button"
								className="ml-[8px] mt-[8px] hidden w-full justify-start rounded-[4px] py-[4px] text-[12px] font-[600] leading-[16px] outline-none md:flex"
								onClick={handleSeeMoreJobsClick}
								style={{
									color: data?.ui_element?.viewall_colour,
								}}
							>
								{data?.view_all_string || "See more"}
								<ChevronRight16
									width="16px"
									height="16px"
									className="ml-[4px]"
									fillColor={data?.ui_element?.viewall_colour}
								/>
							</button>
						)}
					</div>
				</div>
				<div className="relative flex-1 overflow-hidden md:pl-[16px]">
					<div
						className={clsx(
							"absolute left-[4px] top-2/4 z-10 hidden h-[24px] w-[24px] -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-[#000000B3] md:flex",
							{
								"cursor-not-allowed opacity-50": isLeftScrollDisabled,
								"!hidden": data?.children?.length <= 1,
							}
						)}
						onClick={handleScrollLeft}
					>
						<ChevronLeftIcon fill="#fff" />
					</div>
					<div
						className="scrollbarHide scrollbarHide::-webkit-scrollbar flex space-x-[8px] overflow-scroll pr-[16px]"
						ref={scrollContainerRef}
						onScroll={handleScroll}
					>
						{data?.children?.slice(0, 20)?.map((job) => {
							if (job?.type === categoryToTypeMapping.job_card_square)
								return (
									<JobListCard
										job={job?.data}
										fullWidth={false}
										isRenderedInModule
										moduleTrackData={{
											"module title": data?.name,
										}}
									/>
								);
							return null;
						})}
					</div>
					<div
						className={clsx(
							"absolute right-[12px] top-2/4 z-10 hidden h-[24px] w-[24px] -translate-y-1/2 rotate-180 cursor-pointer items-center  justify-center rounded-full bg-[#000000B3] md:flex",
							{
								"cursor-not-allowed opacity-50": isRightScrollDisabled,
								"!hidden": data?.children?.length <= 1,
							}
						)}
						onClick={handleScrollRight}
					>
						<ChevronLeftIcon fill="#fff" />
					</div>
				</div>
				<div
					className={clsx(
						"relative m-auto block h-[2px] w-[48px] bg-[#D1CED4] md:hidden",
						{
							"!hidden": data?.children?.length <= 1,
						}
					)}
				>
					<div
						className="absolute bottom-0 left-0 top-[50%] h-[4px] w-[12px] translate-y-[-50%] rounded-[8px] bg-[#342852]"
						style={{
							marginLeft: `${scrollIndicator}%`,
							background: data?.ui_element?.slider_colour,
						}}
					/>
				</div>
			</div>
			{showUpdateProfileCTA && (
				<div className="block px-[16px] pb-[16px] md:hidden">
					<button
						type="button"
						className="w-full rounded-[4px] border border-solid bg-[#FFFFFF] py-[4px] text-[12px] font-[600] leading-[16px]"
						onClick={handleUpdateProfileClick}
						style={{
							color: data?.ui_element?.text_colour || "#5357CF",
						}}
					>
						{data?.cta?.cta_text || "Update profile"}
					</button>
				</div>
			)}
		</div>
	);
};

export default Type5Module;
